import { ChangeEvent, useState, useEffect } from 'react'
import { Box, Typography, Button, TextField, styled, Grid } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import theme from 'src/utils/themeManagementVersion2'
import { ManagementRegistration } from 'src/types/management/form'
import { managementRegistrationState } from 'src/recoil/management'
import { useRecoilState } from 'recoil'
import ReCAPTCHA from 'react-google-recaptcha'
import PinkTitle from '../../chikamichi/common/PinkTitle'
import toast from 'src/views/toast'
import RegistrationTitle from './RegistrationTitle'

type Props = {
  onSubmit: () => void
}

const ActionButton = styled(Button)({
  color: theme.palette.primary.contrast,
  width: '200px',
  lineHeight: '26px',
})

const SubTitleText = styled(Typography)({
  lineHeight: '32px',
  fontSize: '24px',
  fontWeight: 600,
  color: theme.palette.text.primary,
})

const DemoIframeBg = styled(Box)({
  width: '424px',
  height: '780px',
  border: 'none',
  backgroundImage: 'url(/phone_bg.png)',
  backgroundSize: '424px 780px',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
})

const DemoIframe = styled('iframe')({
  width: '360px',
  height: '720px',
  border: 'none',
  position: 'absolute',
  borderRadius: '35px',
  top: '30px',
  left: '32px',
})

const DemoBalloon = styled(Box)({
  width: '170px',
  height: '120px',
  backgroundColor: theme.palette.primary.main,
  left: '-150px',
  top: '10px',
  position: 'absolute',
  borderRadius: '50%',
  '&:after': {
    content: '""',
    position: 'absolute',
    width: '15px',
    height: '15px',
    backgroundColor: theme.palette.primary.main,
    bottom: '23px',
    right: '-10px',
    transform: 'translateX(-50%) rotate(-55deg)',
    clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
  },
})

const ManagementRegistrationComponent = ({ onSubmit }: Props) => {
  const [captcha, setCaptcha] = useState<string | null>()
  const [values, setValues] = useState<ManagementRegistration>({
    companyName: '',
    name: '',
    email: '',
  })
  const [managementRegistration, setManagementRegistration] = useRecoilState(managementRegistrationState)

  const handleChange = (prop: keyof ManagementRegistration) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value })
    setManagementRegistration({ ...values, [prop]: event.target.value })
  }

  const handleConfirm = () => {
    if (!values.companyName || !values.name || !values.email) {
      toast({ type: 'error', message: '全ての項目を入力してください' })

      return
    }

    onSubmit()
  }

  useEffect(() => {
    setValues(managementRegistration)
  }, [managementRegistration])

  return (
    <ThemeProvider theme={theme}>
      <Box className='content-center' sx={{ backgroundColor: 'transparent' }}>
        <Grid container justifyContent='center' alignItems='center' spacing={4}>
          <Grid item mr={10}>
            <Box sx={{ maxWidth: '400px', width: '100%' }}>
              <Box mb={4}>
                <RegistrationTitle />
              </Box>
              <Box mb={1} sx={{ textAlign: 'center' }}>
                <PinkTitle>\ 無料トライアル /</PinkTitle>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <SubTitleText>新規申し込み</SubTitleText>
              </Box>
              <Box p={2}>
                <form noValidate autoComplete='off' onSubmit={e => e.preventDefault()}>
                  <TextField
                    autoFocus
                    fullWidth
                    value={values.companyName}
                    id='companyName'
                    label='会社名'
                    sx={{ mb: 2 }}
                    onChange={handleChange('companyName')}
                  />
                  <TextField
                    autoFocus
                    fullWidth
                    value={values.name}
                    id='name'
                    label='氏名'
                    sx={{ mb: 2 }}
                    onChange={handleChange('name')}
                  />
                  <TextField
                    autoFocus
                    fullWidth
                    value={values.email}
                    id='email'
                    label='メールアドレス'
                    sx={{ mb: 2 }}
                    onChange={handleChange('email')}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <ReCAPTCHA sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!} onChange={setCaptcha} />
                  </Box>
                  <Box mt={3} sx={{ textAlign: 'center' }}>
                    <ActionButton
                      fullWidth
                      size='large'
                      variant='contained'
                      type='submit'
                      onClick={handleConfirm}
                      disabled={!captcha ? true : false}
                    >
                      内容確認
                    </ActionButton>
                  </Box>
                </form>
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <DemoIframeBg>
              <DemoBalloon px={1} py={4}>
                <Typography
                  variant='body2'
                  color='white'
                  fontWeight='bold'
                  sx={{ lineHeight: '25px', textAlign: 'center' }}
                >
                  デモを操作して
                  <br />
                  学習を体験できます！
                </Typography>
              </DemoBalloon>
              <DemoIframe src='/user/easy_assessment' />
            </DemoIframeBg>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  )
}

export default ManagementRegistrationComponent
